<template>
  <div class="list-users-container">
    <PageHeader 
      pageName="Utilisateurs" 
      pageTitle="Debug: Liste des Utilisateurs" 
      pageSubtitle="Vue de débogage pour visualiser les utilisateurs d'un agent spécifique."
    />
    
    <div class="controls">
      <button @click="loadUsers" :disabled="loading" class="load-btn">
        {{ loading ? 'Chargement...' : 'Charger 20 Utilisateurs' }}
      </button>
      
      <p v-if="loading">... Requête en cours vers /get/strong/users ...</p>
      <p v-if="error" class="error">{{ error }}</p>
    </div>

    <!-- Raw Data Count -->
    <div v-if="users.length > 0" class="results-summary">
      <h3>Résultats: {{ users.length }} utilisateurs trouvés</h3>
    </div>

    <!-- Simple Table -->
    <table v-if="users.length > 0" border="1" cellspacing="0" cellpadding="5">
      <thead>
        <tr>
          <th>ID</th>
          <th>Nom</th>
          <th>Email</th>
          <th>Phone</th>
          <th>Bot Name</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="user in users" :key="user.id">
          <td>{{ user.id }}</td>
          <td>{{ user.name || user.user_full_name }}</td>
          <td>{{ user.email }}</td>
          <td>{{ user.phone || user.tel }}</td>
          <td>{{ user.bot || user.bot_name }}</td>
        </tr>
      </tbody>
    </table>

    <!-- Raw JSON Dump for deep debug -->
    <div v-if="lastResponse" class="raw-json">
      <h3>Raw API Response (Debug):</h3>
      <pre>{{ JSON.stringify(lastResponse, null, 2) }}</pre>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import axios from 'axios';
import { apiBaseUrl } from '../config';
import { consoleLog, consoleError } from '@/utils/logger';
import PageHeader from '@/components/PageHeader.vue';

export default {
  name: 'ListUsers',
  components: {
    PageHeader
  },
  setup() {
    const route = useRoute();
    const users = ref([]);
    const loading = ref(false);
    const error = ref(null);
    const lastResponse = ref(null);
    
    // Get Chatbot ID from URL params
    const chatbotId = route.params.id;

    const loadUsers = async () => {
      loading.value = true;
      error.value = null;
      users.value = [];
      lastResponse.value = null;

      try {
        const userData = localStorage.getItem('user');
        if (!userData) throw new Error("Pas de token utilisateur trouvé (localStorage empty)");
        
        const userObj = JSON.parse(userData);
        const token = userObj.data?.token;

        if (!token) throw new Error("Token manquant dans userObj");

        consoleLog('Sending request to:', `${apiBaseUrl}/get/strong/users`);
        consoleLog('Params:', { page: 1, limit: 20, idbotget: chatbotId });

        const response = await axios.get(`${apiBaseUrl}/get/strong/users`, {
          params: {
            page: 1,
            limit: 20,
            idbotget: chatbotId
          },
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        consoleLog('Response received:', response);
        lastResponse.value = response.data;

        if (response.data && response.data.users) {
           users.value = response.data.users;
        } else {
           error.value = "Pas de tableau 'users' dans la réponse.";
        }

      } catch (err) {
        consoleError("Erreur loadUsers:", err);
        error.value = err.message || "Erreur inconnue";
        if (err.response) {
            error.value += ` (${err.response.status} - ${err.response.statusText})`;
        }
      } finally {
        loading.value = false;
      }
    };

    return {
      users,
      loading,
      error,
      loadUsers,
      lastResponse
    };
  }
};
</script>

<style scoped>
.list-users-container {
  padding: 20px;
  font-family: monospace;
}
.load-btn {
  padding: 10px 20px;
  background-color: #25D366;
  color: white;
  border: none;
  font-size: 16px;
  cursor: pointer;
  margin-bottom: 20px;
}
.load-btn:disabled {
  background-color: #ccc;
}
.error {
  color: red;
  font-weight: bold;
}
.raw-json {
  background: #f4f4f4;
  padding: 10px;
  margin-top: 20px;
  max-height: 300px;
  overflow: auto;
}
table {
  width: 100%;
  border-collapse: collapse;
}
</style>
